/* Copyright (c) 2017 Backpack Health LLC */

import { authorizedApiRequest, authorizedApiRequestBlob, apiRequest, externalApi, getCancelToken } from './apiRequest';
import userGroupRole from '../constants/userGroupRole';

const api = {
  create: function createGroup() {
    return apiRequest('post', 'groups');
  },

  getMetadata: function getGroupMetadata(subdomain) {
    return apiRequest('get', `groups/metadata?subdomain=${subdomain}`);
  },

  get: function getGroup(groupId) {
    return authorizedApiRequest('get', `groups/${groupId}`);
  },

  update: function updateGroup(groupId) {
    return authorizedApiRequest('put', `groups/${groupId}`);
  },

  delete: function deleteGroup(groupId) {
    return authorizedApiRequest('delete', `groups/${groupId}`);
  },

  getAllAdmins: function getAllGroupAdmins(groupId) {
    return authorizedApiRequest('get', `groups/${groupId}/admins`);
  },

  getAdmin: function getGroupAdmins(groupId, userId) {
    return authorizedApiRequest('get', `groups/${groupId}/admins/${userId}`);
  },

  updateAdmin: function updateGroupAdmin(groupId, userId) {
    return authorizedApiRequest('put', `groups/${groupId}/admins/${userId}`);
  },

  groupStats: function getGroupStats(groupId) {
    return authorizedApiRequest('get', `groups/${groupId}/stats`);
  },

  downloadSurveyData: function downloadSurveyData(groupId, surveyId) {
    return authorizedApiRequestBlob('get', `groups/${groupId}/surveyData/${surveyId}`);
  }

};


const groupApi = {
  create(data) {
    return api.create()(data)
    .then(res => res.data.data);
  },

  getMetadata(subdomain) {
    return api.getMetadata(subdomain)()
    .then(res => res.data.data);
  },

  get(groupId) {
    return api.get(groupId)()
    .then(res => res.data.data);
  },

  update(groupData) {
    return api.update(groupData.id)(groupData)
    .then(res => null);
  },

  delete(groupId) {
    return api.delete(groupId)()
    .then(res => null);
  },

  getAllAdmins(groupId) {
    return api.getAllAdmins(groupId)()
    .then(res => res.data.data);
  },

  getAdmin(groupId, userId) {
    return api.getAdmin(groupId, userId)()
    .then(res => res.data.data);
  },

  deactivateAdmin(groupId, userId) {
    return api.updateAdmin(groupId, userId)({ state: 'inactive' })
  },

  reactivateAdmin(groupId, userId) {
    return api.updateAdmin(groupId, userId)({ state: 'active', role: userGroupRole.VIEWER })
  },

  changeRole(groupId, userId, role) {
    return api.updateAdmin(groupId, userId)({ role,  state: 'active' });
  },

  getStats(groupId) {
    return api.groupStats(groupId)()
      .then(res => res.data);
  },

  downloadSurveyData(groupId, surveyId) {
    return api.downloadSurveyData(groupId, surveyId)()
      .then(res => {
        const disposition = res.headers['content-disposition'];
        const fileName = decodeURI(disposition.match(/filename="(.+)"/)[1]);
        const { data: blob } = res;

        return { blob, fileName };
      });
  },

  getDataExportList(groupId) {
    return authorizedApiRequest('get', `groups/${groupId}/dataExports`)()
      .then(res => res.data);
  },

  requestNewDataExport(groupId) {
    return authorizedApiRequest('post', `groups/${groupId}/dataExports`)()
      .then(res => res.data.data);
  },

  getDataExport(groupId, exportId) {
    return authorizedApiRequest('get', `groups/${groupId}/dataExports/${exportId}`)()
      .then(res => res.data.data);
  },

  createPasswordProtectedArchive(groupId, exportId, password, format) {
    return authorizedApiRequestBlob('post', `groups/${groupId}/dataExports/${exportId}/archive`)
      ({ password, format })
      .then(res => {
        const disposition = res.headers['content-disposition'];
        const fileName = decodeURI(disposition.match(/filename\*=utf-8''(.+)/i)[1]);
        const { data: blob } = res;

        return { blob, fileName };
      });
  },

  createFile(groupId, fileData, config) {
    return authorizedApiRequest('post', `groups/${groupId}/files`)(fileData, null, config)
      .then(res => res.data.data);
  },
  getUploadUrl(groupId, fileId, config) {
    return authorizedApiRequest('get', `groups/${groupId}/files/${fileId}/uploadUrl`, null, config)()
      .then(res => res.data.url);
  },
  markFileUploadComplete(groupId, fileId, config) {
    return authorizedApiRequest('put', `groups/${groupId}/files/${fileId}/completed`, null, config)({ completed: true })
      .then(res => res.data.data);
  },

  createAndUploadFile(groupId, fileData, fileBytes, progressCallback, onComplete) {
    const source = getCancelToken();
    let fileId = -1;

    this.createFile(groupId, fileData, { cancelToken: source.token })
    .then(newFileData => {
      fileId = newFileData.id;
      return this.getUploadUrl(groupId, fileId, { cancelToken: source.token });
    }).then(uploadUrl => {
      return externalApi('put', uploadUrl, null, {
        'Content-Type': fileData.mime_type,
      })(fileBytes, {
        onUploadProgress: (p) => {
          const progress = Number.parseInt((p.loaded / p.total) * 100);
          progressCallback(progress);
        },
        cancelToken: source.token,
      })
    }).then(res => {
      return this.markFileUploadComplete(groupId, fileId, { cancelToken: source.token });
    }).then(() => {
      onComplete({ fileId });
    })
    .catch(err => {
      console.log(err)
    });

    return {
      abort: () => {
        source.cancel('Operation canceled by the user.');
      },
    }
  },

};

export default groupApi;
